import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { BookOpen, CheckSquare, ExternalLink, FileDown, Printer, QrCode, Square, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { openServerPdf } from "../../lib/api";
import ProviderPromo from "../../components/ProviderPromo";
import { Button, Card, Field, Select, Spinner } from "../../components/ui-kit";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";
import { setPrintPageSize, clearPrintPageSize } from "../../lib/printPageSize";
import { orderIngredientsByWeight } from "../../lib/ingredientOrder";
import { isIOS } from "../../lib/platform";
import { elementToPdf, openServerPdfFile, pdfFileName, preparePdfWindow } from "../../lib/pdfExport";
import { conservationDisplayText } from "../../lib/conservationText";
import { QrLabel62x100Sheet, printQrLabels62x100, qrLabels62x100Pdf } from "../../components/QrLabel62x100";

const MODES = [
  { key: "single", label: "QR singolo 29×90", icon: QrCode, description: "Cartellino per il contenitore esposto in vetrina." },
  { key: "label62", label: "Etichette QR 62×100", icon: Tag, description: "Un'etichetta adesiva 62×100 per ogni ricetta selezionata." },
  { key: "a4", label: "Tavola QR formato A4", icon: CheckSquare, description: "Tutti i QR con nome e istruzioni, pronti per il quadretto." },
  { key: "book", label: "Libro ingredienti classico", icon: BookOpen, description: "Schede complete tradizionali con ingredienti, allergeni e QR." },
];

export default function LibroIngredienti() {
  const { ctx } = useOutletContext();
  const [searchParams] = useSearchParams();
  const [recipes, setRecipes] = useState(null);
  const requestedMode = searchParams.get("mode");
  const [mode, setMode] = useState(MODES.some((m) => m.key === requestedMode) ? requestedMode : "a4");
  const [selectedId, setSelectedId] = useState("");
  const [selected, setSelected] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const sheetRef = useRef(null);

  useEffect(() => {
    api.get("/app/catalog/recipes?status=approvata").then(({ data }) => {
      const rows = data.recipes || [];
      setRecipes(rows);
      setSelectedId(rows[0]?.id || "");
      setSelected(rows.map((r) => r.id));
    }).catch(() => setRecipes([]));
  }, []);

  const chosen = useMemo(() => (recipes || []).filter((r) => selected.includes(r.id)), [recipes, selected]);
  const singleRecipe = (recipes || []).find((r) => r.id === selectedId);
  const printable = mode === "single" ? (singleRecipe ? [singleRecipe] : []) : chosen;
  const toggle = (id) => setSelected((current) => current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  const toggleAll = () => setSelected(selected.length === (recipes || []).length ? [] : (recipes || []).map((r) => r.id));
  const print = () => {
    if (!printable.length) return;
    if (mode === "label62") { printQrLabels62x100(); return; }
    if (mode === "single" && isIOS()) {
      // iOS: PDF vero invece di window.print(). Safari aggiunge sempre un
      // header/footer (URL, data, numero pagina) quando si stampa una pagina
      // web, sovrapponendosi al cartellino e spingendolo su un secondo foglio
      // fantasma. La scheda si apre subito al tocco: nessun blocco popup di Safari.
      openServerPdf(`/app/catalog/recipes/${encodeURIComponent(singleRecipe.id)}/qr-label.pdf`);
      return;
    }
    // Android/desktop: window.print() diretto, più affidabile del PDF qui perché il
    // gestore PDF su Android varia troppo da dispositivo a dispositivo.
    document.body.setAttribute("data-print", `ingredient-${mode}`);
    setPrintPageSize(...(mode === "single" ? [90, 29] : [210, 297]));
    setTimeout(() => {
      window.print();
      document.body.removeAttribute("data-print");
      clearPrintPageSize();
    }, 60);
  };

  // PDF vero, da aprire e stampare: dal telefono è più affidabile della stampa
  // diretta della pagina (vedi lib/pdfExport.js). Il cartellino singolo usa il
  // PDF già generato dal backend; tavola A4 e libro si creano dall'anteprima.
  const createPdf = async () => {
    if (!printable.length) return;
    const win = preparePdfWindow();
    setPdfBusy(true);
    try {
      if (mode === "single") {
        await openServerPdfFile(`/app/catalog/recipes/${encodeURIComponent(singleRecipe.id)}/qr-label.pdf`, { fileName: pdfFileName("cartellino-qr", singleRecipe.name), win });
      } else if (mode === "label62" && sheetRef.current) {
        await qrLabels62x100Pdf(sheetRef.current, win, "ricette", ctx.tenant?.name);
      } else if (sheetRef.current) {
        await elementToPdf(sheetRef.current.firstElementChild, mode === "book"
          ? { fileName: pdfFileName("libro-ingredienti", ctx.tenant?.name), pageSelector: ".ingredient-book-cover, .ingredient-recipe-sheet", win }
          : { fileName: pdfFileName("tavola-qr-ingredienti", ctx.tenant?.name), win });
      }
    } catch {
      toast.error("Impossibile creare il PDF. Riprova.");
    } finally {
      setPdfBusy(false);
    }
  };

  if (!recipes) return <Spinner />;

  return (
    <div className="space-y-6" data-testid="ingredient-book-page">
      <div className="flex flex-wrap items-end justify-between gap-4 no-print">
        <div><p className="text-xs uppercase tracking-[0.18em] text-slate-400">Documenti</p><div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-3xl font-bold">Libro degli ingredienti</h1><AcademyTitleBadge /></div><p className="mt-2 max-w-3xl text-sm text-slate-500">Crea cartellini QR per la vetrina, una tavola riepilogativa A4 oppure il libro ingredienti completo in formato tradizionale.</p></div>
        <div className="flex flex-wrap gap-3">
          <Button variant="success" onClick={createPdf} loading={pdfBusy} disabled={!printable.length} data-testid="ingredient-book-pdf">{!pdfBusy && <FileDown className="h-4 w-4" />} {pdfBusy ? "Creo il PDF…" : "Crea PDF e stampa"}</Button>
          <Button variant="outline" onClick={print} disabled={!printable.length} data-testid="ingredient-book-print"><Printer className="h-4 w-4" /> Stampa diretta {mode === "single" ? "cartellino" : mode === "label62" ? "etichette" : mode === "a4" ? "tavola A4" : "libro completo"}</Button>
        </div>
      </div>

      {recipes.length === 0 ? <Card className="p-10 text-center text-slate-500">Non ci sono ricette approvate. Approva almeno una ricetta per creare il libro ingredienti.</Card> : <>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 no-print">
          {MODES.map(({ key, label, icon: Icon, description }) => <button key={key} type="button" onClick={() => setMode(key)} className={`rounded-xl border p-4 text-left ${mode === key ? "border-gold bg-gold/10 ring-2 ring-gold/20" : "border-hairline bg-white"}`} data-testid={`ingredient-book-mode-${key}`}><span className="flex items-center gap-2 font-heading font-semibold text-navy"><Icon className="h-5 w-5 text-gold" />{label}</span><span className="mt-1 block text-xs text-slate-500">{description}</span></button>)}
        </div>

        <Card className="p-5 no-print">
          {mode === "single" ? <div className="flex flex-wrap items-end gap-3"><div className="min-w-[260px] flex-1"><Field label="Produzione da stampare"><Select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>{recipes.map((r) => <option key={r.id} value={r.id}>{r.name?.toUpperCase()}</option>)}</Select></Field></div>{singleRecipe && <a href={qrUrl(singleRecipe)} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-navy px-5 py-2.5 text-sm font-medium text-navy hover:bg-slate-50"><ExternalLink className="h-4 w-4" /> Verifica scheda QR</a>}</div> : <>
            <button type="button" onClick={toggleAll} className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-navy">{selected.length === recipes.length ? <CheckSquare className="h-5 w-5 text-emerald-600" /> : <Square className="h-5 w-5" />} {selected.length === recipes.length ? "Deseleziona tutte" : "Seleziona tutte"}</button>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{recipes.map((r) => <label key={r.id} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm ${selected.includes(r.id) ? "border-emerald-300 bg-emerald-50" : "border-hairline"}`}><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} className="h-5 w-5 accent-emerald-600" /><span className="font-medium text-navy">{r.name?.toUpperCase()}</span></label>)}</div>
          </>}
        </Card>

        {mode === "single" && singleRecipe && <QrLabel recipe={singleRecipe} />}
        {/* Copia dedicata al flusso window.print() (Android/desktop), portalata in
            document.body: <main class="app-content"> ha overflow-x-clip, containing block dei
            discendenti position:absolute (stesso motivo del cartellino di produzione in
            Etichette.jsx). Su iOS resta inutilizzata (si stampa il PDF generato dal backend). */}
        {mode === "single" && singleRecipe && createPortal(
          <div className="ingredient-single-print print-area"><QrLabel recipe={singleRecipe} /></div>,
          document.body
        )}
        {mode === "label62" && chosen.length > 0 && <QrLabel62x100Sheet sheetRef={sheetRef} labels={chosen.map((recipe) => ({
          key: recipe.id,
          url: qrUrl(recipe),
          kicker: "Ingredienti e allergeni",
          title: recipe.name?.toUpperCase(),
          scanText: "INQUADRA IL QR CODE PER CONOSCERE INGREDIENTI E ALLERGENI",
          footer: ctx.tenant?.name?.toUpperCase(),
        }))} />}
        {(mode === "a4" || mode === "book") && (
          <div className="ingredient-book-print print-area" ref={sheetRef}>
            {mode === "a4" && <A4QrSheet recipes={chosen} company={ctx.tenant?.name} />}
            {mode === "book" && <ClassicBook recipes={chosen} company={ctx.tenant?.name} tenant={ctx.tenant} />}
          </div>
        )}
      </>}
    </div>
  );
}

const qrUrl = (recipe) => `${window.location.origin}/ingredienti/${recipe.public_token}`;

// Nel libro ingredienti stampabile si mostra solo la modalità di
// conservazione, senza la fase produttiva (vedi lib/conservationText.js).
function publicConservationText(text) {
  if (!text) return text;
  const result = conservationDisplayText(text);
  // Regime "congelato" (es. "CONSERVARE CONGELATO A -18 °C O TEMPERATURA
  // INFERIORE", generato da ConservationWizard.jsx): nella versione
  // stampabile si mostra con la formulazione richiesta, "Conservare a
  // temperatura ≤ {temperatura} °C.", mantenendo comunque la temperatura
  // effettiva della ricetta invece di un valore fisso.
  const frozen = result.match(/CONGELAT\w*[\s\S]*?(-?\d+(?:[.,]\d+)?)\s*°?\s*C/i);
  if (frozen) {
    return `Conservare a temperatura ≤ ${frozen[1].replace(",", ".")} °C.`;
  }
  return result;
}

// "Indicazioni di consumo" è testo libero facoltativo: se vuoto o
// equivalente a "non specificate", la sezione non va mostrata affatto nella
// versione stampabile (vedi RecipeSheet), invece del vecchio segnaposto
// "NON SPECIFICATE".
function hasConsumptionText(text) {
  if (!text) return false;
  const normalized = text.trim().replace(/\.+$/, "").toUpperCase();
  return normalized.length > 0 && normalized !== "NON SPECIFICATE" && normalized !== "NON SPECIFICATA";
}

function QrLabel({ recipe }) {
  return <div className="ingredient-qr-label bg-white text-navy" data-testid="ingredient-single-label"><div className="min-w-0 flex-1"><div className="font-heading text-[13px] font-bold leading-tight">{recipe.name?.toUpperCase()}</div><p className="mt-1 text-[9px] font-semibold leading-tight">INQUADRA IL QR CODE PER CONOSCERE INGREDIENTI E ALLERGENI</p></div><QRCodeCanvas value={qrUrl(recipe)} size={82} fgColor="#0B1D3A" /> </div>;
}

function A4QrSheet({ recipes, company }) {
  return <section className="ingredient-a4-sheet bg-white text-navy"><header className="mb-5 border-b-2 border-navy pb-3 text-center"><h2 className="font-heading text-3xl font-bold">LIBRO DIGITALE DEGLI INGREDIENTI</h2><p className="mt-1 text-sm font-semibold">{company?.toUpperCase()}</p><p className="mt-2 text-xs">INQUADRA IL QR CODE DEL PRODOTTO PER CONSULTARE INGREDIENTI E ALLERGENI</p></header><div className="grid grid-cols-2 gap-4 sm:grid-cols-3 print:grid-cols-3">{recipes.map((recipe) => <div key={recipe.id} className="min-w-0 break-inside-avoid rounded-lg border border-navy/30 p-3 text-center"><QRCodeCanvas value={qrUrl(recipe)} size={112} fgColor="#0B1D3A" className="mx-auto aspect-square h-auto max-w-full" /><div className="mt-2 font-heading text-sm font-bold leading-tight [overflow-wrap:anywhere]">{recipe.name?.toUpperCase()}</div><div className="mt-1 text-[9px] font-semibold">INGREDIENTI E ALLERGENI</div></div>)}</div><footer className="mt-5 border-t border-navy/20 pt-2 text-center text-[9px]">Le informazioni sono disponibili anche nel libro ingredienti completo presente nell’esercizio.</footer></section>;
}

function ClassicBook({ recipes, company, tenant }) {
  return <section className="ingredient-classic-book bg-white text-navy"><div className="ingredient-book-cover"><h2 className="font-heading text-4xl font-bold">LIBRO DEGLI INGREDIENTI</h2><p className="mt-3 text-lg font-semibold">{company?.toUpperCase()}</p><p className="mt-6 max-w-xl text-center text-sm">ELENCO DELLE PREPARAZIONI CON INGREDIENTI, ALLERGENI, MODALITÀ DI CONSERVAZIONE E ACCESSO DIGITALE TRAMITE QR CODE.</p></div>{recipes.map((recipe) => <RecipeSheet key={recipe.id} recipe={recipe} tenant={tenant} />)}</section>;
}

// Identificazione OSA (ragione sociale, indirizzo, P.IVA) dall'anagrafica
// azienda già esistente — mai inserita a mano nella ricetta — e footer
// discreto "fornito da DF Consulting Group", separato graficamente e mai
// confondibile con i dati dell'operatore alimentare. Presenti solo qui e
// nella pagina pubblica del QR (vedi IngredientBookPublic.jsx): non nelle
// etichette dei prodotti preimballati, che restano invariate.
function OsaAndProviderFooter({ tenant }) {
  const address = tenant?.contacts?.address;
  const vat = tenant?.fiscal?.vat;
  return <div className="mt-5 space-y-3">
    <div className="border-t border-navy/20 pt-3 text-xs text-slate-600">
      <p className="font-semibold uppercase text-navy">{tenant?.name}</p>
      {address && <p>{address}</p>}
      {vat && <p>P. IVA {vat}</p>}
    </div>
    <ProviderPromo />
  </div>;
}

function RecipeSheet({ recipe, tenant }) {
  const ordered = orderIngredientsByWeight(recipe.ingredients);
  return <article className="ingredient-recipe-sheet"><div className="flex items-start justify-between gap-6 border-b-2 border-navy pb-4"><div><p className="text-xs font-semibold uppercase tracking-widest text-gold">Scheda prodotto</p><h3 className="mt-1 font-heading text-3xl font-bold">{recipe.name?.toUpperCase()}</h3><p className="mt-2 text-xs">VERSIONE {recipe.version} · RICETTA APPROVATA</p></div><div className="text-center"><QRCodeCanvas value={qrUrl(recipe)} size={112} fgColor="#0B1D3A" className="mx-auto aspect-square h-auto max-w-full" /><p className="mt-1 text-[9px] font-semibold">CONSULTA ONLINE</p></div></div><div className="mt-5"><h4 className="text-sm font-bold uppercase tracking-wide">Ingredienti</h4><p className="mt-2 text-sm leading-relaxed">{ordered.map((item, index) => <React.Fragment key={`${item.ingredient_id}-${index}`}>{index > 0 ? ", " : ""}<span>{item.name || item.ingredient_id}</span>{item.allergens?.length > 0 && <strong> ({item.allergens.map((key) => allergenLabel(recipe, key)).join(", ").toUpperCase()})</strong>}</React.Fragment>)}</p></div>{recipe.allergen_labels?.length > 0 && <div className="mt-5 rounded-lg border-2 border-red-500 p-3"><h4 className="text-sm font-bold uppercase text-red-700">Allergeni</h4><p className="mt-1 text-sm font-bold uppercase text-red-700">{recipe.allergen_labels.join(", ")}</p></div>}{recipe.trace_labels?.length > 0 && <div className="mt-3 text-sm"><strong>Può contenere:</strong> {recipe.trace_labels.join(", ")}</div>}<dl className={`mt-5 grid gap-3 text-sm ${hasConsumptionText(recipe.consumption) ? "grid-cols-2" : "grid-cols-1"}`}><div><dt className="font-bold uppercase">Conservazione</dt><dd>{publicConservationText(recipe.conservation) || "NON SPECIFICATA"}</dd></div>{hasConsumptionText(recipe.consumption) && <div><dt className="font-bold uppercase">Indicazioni di consumo</dt><dd>{recipe.consumption}</dd></div>}</dl><p className="mt-8 border-t border-navy/20 pt-3 text-[10px]">Le informazioni relative alla presenza di sostanze o prodotti che provocano allergie o intolleranze sono disponibili nel presente Libro Ingredienti. Per ulteriori informazioni rivolgersi al personale in servizio.</p><OsaAndProviderFooter tenant={tenant} /></article>;
}

function allergenLabel(recipe, key) {
  const index = (recipe.allergens || []).indexOf(key);
  return (recipe.allergen_labels || [])[index] || key;
}
